import { useState, useEffect, useCallback } from 'react';
import type {
  DoctorShiftRule,
  DoctorShiftRuleGrouped,
  RuleType,
  TimeCondition
} from '../../../../domain/doctorShiftRule';
import type {
  DoctorShiftRuleFilter,
  DoctorShiftRuleRepository
} from '../../../../application/doctorShiftRule';
import {
  fetchDoctorShiftRules,
  createDoctorShiftRule,
  updateDoctorShiftRule,
  deleteDoctorShiftRule
} from '../../../../application/doctorShiftRule';

/**
 * 医師シフトルール一覧 状態型
 */
export interface DoctorShiftRuleListState {
  data: DoctorShiftRuleGrouped[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  filters: DoctorShiftRuleFilter;
  // モーダル状態
  addModalOpen: boolean;
  editModalOpen: boolean;
  deleteDialogOpen: boolean;
  // 選択状態
  selectedRule: DoctorShiftRule | null;
  selectedGroupedRule: DoctorShiftRuleGrouped | null;
}

/**
 * 医師シフトルール一覧 Hook戻り値型
 */
export interface UseDoctorShiftRuleListReturn extends DoctorShiftRuleListState {
  // データ操作
  fetchRules: (filters?: DoctorShiftRuleFilter) => Promise<void>;
  updateFilters: (newFilters: Partial<DoctorShiftRuleFilter>) => void;
  handlePageChange: (page: number) => void;
  handleSearch: (search: string) => void;
  refresh: () => void;
  // モーダル操作
  showAddModal: () => void;
  showEditModal: (rule: DoctorShiftRule, groupedRule?: DoctorShiftRuleGrouped) => void;
  showDeleteDialog: (rule: DoctorShiftRule) => void;
  closeModals: () => void;
  // CRUD操作
  createRule: (data: {
    doctorIds: string[];
    ruleType: RuleType;
    timeConditions: TimeCondition[];
  }) => Promise<boolean>;
  updateRule: (id: string, data: {
    ruleType?: RuleType;
    timeConditions?: TimeCondition[];
  }) => Promise<boolean>;
  deleteRule: (id: string) => Promise<boolean>;
  // ユーティリティ
  clearError: () => void;
}

/**
 * 医師シフトルール一覧管理用カスタムフック
 *
 * 意図：
 * - 医師シフトルールデータの一覧取得・CRUD操作を統合管理
 * - フィルタリング・ページネーション機能
 * - モーダル・ダイアログの状態管理
 */
export const useDoctorShiftRuleList = (
  repository: DoctorShiftRuleRepository
): UseDoctorShiftRuleListReturn => {
  const [state, setState] = useState<DoctorShiftRuleListState>({
    data: [],
    meta: null,
    isLoading: false,
    error: null,
    filters: {
      page: 1,
      pageSize: 100
    },
    addModalOpen: false,
    editModalOpen: false,
    deleteDialogOpen: false,
    selectedRule: null,
    selectedGroupedRule: null
  });

  /**
   * シフトルール一覧を取得
   */
  const fetchRules = useCallback(async (filters?: DoctorShiftRuleFilter) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const mergedFilters = { ...state.filters, ...filters };
      const { result } = await fetchDoctorShiftRules(repository, { filter: mergedFilters });

      setState(prev => ({
        ...prev,
        data: result.data,
        meta: result.meta,
        isLoading: false,
        filters: mergedFilters
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'データ取得に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
    }
  }, [repository, state.filters]);

  /**
   * 初回データ取得
   */
  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * フィルター更新
   */
  const updateFilters = useCallback((newFilters: Partial<DoctorShiftRuleFilter>) => {
    const updatedFilters = { ...state.filters, ...newFilters };
    fetchRules(updatedFilters);
  }, [fetchRules, state.filters]);

  /**
   * ページ変更
   */
  const handlePageChange = useCallback((page: number) => {
    updateFilters({ page });
  }, [updateFilters]);

  /**
   * 検索実行
   */
  const handleSearch = useCallback((search: string) => {
    updateFilters({ search, page: 1 });
  }, [updateFilters]);

  /**
   * データリフレッシュ
   */
  const refresh = useCallback(() => {
    fetchRules(state.filters);
  }, [fetchRules, state.filters]);

  /**
   * 追加モーダル表示
   */
  const showAddModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      addModalOpen: true,
      selectedRule: null,
      selectedGroupedRule: null
    }));
  }, []);

  /**
   * 編集モーダル表示
   */
  const showEditModal = useCallback((rule: DoctorShiftRule, groupedRule?: DoctorShiftRuleGrouped) => {
    setState(prev => ({
      ...prev,
      editModalOpen: true,
      selectedRule: rule,
      selectedGroupedRule: groupedRule || null
    }));
  }, []);

  /**
   * 削除確認ダイアログ表示
   */
  const showDeleteDialog = useCallback((rule: DoctorShiftRule) => {
    setState(prev => ({
      ...prev,
      deleteDialogOpen: true,
      selectedRule: rule
    }));
  }, []);

  /**
   * モーダル・ダイアログを閉じる
   */
  const closeModals = useCallback(() => {
    setState(prev => ({
      ...prev,
      addModalOpen: false,
      editModalOpen: false,
      deleteDialogOpen: false,
      selectedRule: null,
      selectedGroupedRule: null
    }));
  }, []);

  /**
   * シフトルール作成
   */
  const createRule = useCallback(async (data: {
    doctorIds: string[];
    ruleType: RuleType;
    timeConditions: TimeCondition[];
  }): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const result = await createDoctorShiftRule(repository, data);

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || '作成に失敗しました'
        }));
        return false;
      }

      await fetchRules(state.filters);
      closeModals();

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '作成に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [repository, fetchRules, state.filters, closeModals]);

  /**
   * シフトルール更新
   */
  const updateRule = useCallback(async (id: string, data: {
    ruleType?: RuleType;
    timeConditions?: TimeCondition[];
  }): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const result = await updateDoctorShiftRule(repository, { id, ...data });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || '更新に失敗しました'
        }));
        return false;
      }

      await fetchRules(state.filters);
      closeModals();

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '更新に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [repository, fetchRules, state.filters, closeModals]);

  /**
   * シフトルール削除
   */
  const deleteRule = useCallback(async (id: string): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const result = await deleteDoctorShiftRule(repository, { id });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || '削除に失敗しました'
        }));
        return false;
      }

      await fetchRules(state.filters);
      closeModals();

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '削除に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [repository, fetchRules, state.filters, closeModals]);

  /**
   * エラーをクリア
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    // 状態
    ...state,

    // データ操作
    fetchRules,
    updateFilters,
    handlePageChange,
    handleSearch,
    refresh,

    // モーダル操作
    showAddModal,
    showEditModal,
    showDeleteDialog,
    closeModals,

    // CRUD操作
    createRule,
    updateRule,
    deleteRule,

    // ユーティリティ
    clearError
  };
};
